import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { HardHat, Loader2 } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone_number: "", password: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Workspace access created");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-lg fade-in">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-12 w-12 grid place-items-center bg-primary rounded-md">
            <HardHat className="h-7 w-7 text-primary-foreground" strokeWidth={2.5} />
          </div>
          <div className="font-display text-xl font-black tracking-tight">
            CONSTRUCTVISION<span className="text-primary">-3D</span>
          </div>
        </div>

        <h1 className="font-display text-3xl font-black tracking-tight mb-2">Create access</h1>
        <p className="text-muted-foreground mb-8">Register to join or launch project workspaces.</p>

        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="label-caps">First name</Label>
              <Input required value={form.first_name} onChange={set("first_name")}
                data-testid="register-firstname-input" className="h-12 bg-card" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Last name</Label>
              <Input required value={form.last_name} onChange={set("last_name")}
                data-testid="register-lastname-input" className="h-12 bg-card" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Email</Label>
            <Input type="email" required value={form.email} onChange={set("email")}
              data-testid="register-email-input" className="h-12 bg-card" placeholder="you@company.com" />
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Phone (optional)</Label>
            <Input value={form.phone_number} onChange={set("phone_number")}
              data-testid="register-phone-input" className="h-12 bg-card" />
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Password</Label>
            <Input type="password" required minLength={6} value={form.password} onChange={set("password")}
              data-testid="register-password-input" className="h-12 bg-card" placeholder="Min 6 characters" />
          </div>
          <Button type="submit" disabled={loading} data-testid="register-submit-button"
            className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-base">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create Account"}
          </Button>
        </form>

        <p className="text-muted-foreground mt-8 text-sm">
          Already have access?{" "}
          <Link to="/login" data-testid="go-login-link" className="text-primary font-semibold hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
